import React from "react"

import { TimeLimitApi } from "../services/TimeLimitApi"

import TimeLimit from "../types/TimeLimit"

import {MessageInterface} from "../components/common/Message";

export type TimeLimitContextType = {
    addItem:      (timeLimit: TimeLimit) => Promise<TimeLimit | void> | void
    deleteItem:   (timeLimit: TimeLimit) => Promise<any> | void
    editItem:     (timeLimit: TimeLimit) => Promise<TimeLimit | void> | void
    syncItem:     (timeLimit: TimeLimit, soft?: boolean) => Promise<TimeLimit  | void> | void
    fetchItem:    (id: number, include?: string) => void
    fetchItems:   (search?: string, force?: boolean) => void
    items:        TimeLimit[] | undefined;
    loadingItems: boolean;

    inProgress:   boolean;

    message: MessageInterface | undefined
    closeMessage: () => void
}

const initialState = {
    addItem:      () => {},
    deleteItem:   () => {},
    editItem:     () => {},
    syncItem:     () => {},
    fetchItem:    () => {},
    fetchItems:   () => {},
    items:        undefined,
    loadingItems: true,

    inProgress:   false,

    message:      undefined,
    closeMessage: () => {},

    fetchSelectOptions: () => {},
    selectOptions: undefined,
}

export const TimeLimitContext = React.createContext<TimeLimitContextType>(initialState);

interface TimeLimitProviderProps {
    children: React.ReactNode
}

const TimeLimitProvider: React.FC<TimeLimitProviderProps> = ( { children }) => {

    const [items, setItems] = React.useState<TimeLimit[] | undefined>(initialState.items)
    const [loadingItems, setLoadingItems] = React.useState<boolean>(initialState.loadingItems)
    const [inProgress, setInProgress] = React.useState<boolean>(initialState.inProgress)
    const [message, setMessage] = React.useState<MessageInterface | undefined>(initialState.message)

    const fetchItem = async (id: number, include?: string): Promise<TimeLimit | void> => {
        setLoadingItems(true)

        const result = await TimeLimitApi.get(id, include)
            .catch((e) => setErrorMessage(e))

        setLoadingItems(false)

        return result
    }

    const fetchItems = async (search?: string, force: boolean = false) => {
        if (items === undefined || force) {
            setLoadingItems(true)

            const result = await TimeLimitApi.getAll(search)
                .catch((e) => setErrorMessage(e))

            if (result) {
                setItems(result)
                setLoadingItems(false)
            }
        }
    }

    const addItem = async (newTimeLimit: TimeLimit): Promise<TimeLimit | void> => {
        closeMessage()
        setInProgress(true)

        const result = await TimeLimitApi.add(newTimeLimit)
            .catch(e => setErrorMessage(e))

        if (items && result) {
            setInProgress(false)
            setItems([result, ...items])
        }

        return result
    }

    const editItem = async (item: TimeLimit): Promise<TimeLimit | void> => {
        closeMessage()
        setInProgress(true)

        const result = await TimeLimitApi.edit(item)
            .catch((e) => setErrorMessage(e))

        if (items && result) {
            const newItems: TimeLimit[] = items.map(oldItem => {
                return (oldItem.id === item.id && result.id) ? {...result} : oldItem
            })

            setInProgress(false)
            setItems(newItems)
        }

        return result
    }

    const deleteItem = async (item: TimeLimit): Promise<any> => {
        closeMessage()
        setInProgress(true)

        const result = await TimeLimitApi.delete(item.id)
            .catch((e) => setErrorMessage(e))

        if (items && result !== undefined) {
            const newItems = [...items]
            newItems.splice(newItems.findIndex(i => i.id === item.id), 1)

            setInProgress(false)
            setItems(newItems)
        }
    }

    const syncItem = async (item: TimeLimit, softSync: boolean = false): Promise<TimeLimit | void> => {
        closeMessage()

        if (inProgress) return

        setInProgress(true)

        const newItem: TimeLimit | void = await TimeLimitApi.syncItemById(item, softSync)
            .catch((e) => setErrorMessage(e))

        if (newItem) {
            setInProgress(false);

            return newItem
        }
    }

    const closeMessage = () => setMessage(undefined)

    const setErrorMessage = (e: any) => {
        setMessage({
            text: e.response.data.message || '?',
            type: 'error'
        })

        setInProgress(false)
        setLoadingItems(false)
    }

    return (
        <TimeLimitContext.Provider
            value={{

                addItem,
                deleteItem,
                editItem,
                syncItem,
                fetchItem,
                fetchItems,
                items,
                loadingItems,
                inProgress,

                message,
                closeMessage,
            }}
        >
            {children}
        </TimeLimitContext.Provider>
    );
}

export const useTimeLimits = () => {
    return React.useContext(TimeLimitContext) as TimeLimitContextType
}

export default TimeLimitProvider